/**
 * CarouselPage.jsx — Carousel Composer (/carousel): Instagram carousel slides
 * from your own frames. Photos are read in the browser and never uploaded;
 * slides export as JPEG/PNG straight from the originals.
 *
 * Full bleed like RotoTube: negative margins cancel <main>'s padding and the page
 * paints its own grading-suite grey. The library view sizes itself to main's
 * visible area (minus the measured header, and the tab bar on mobile), so only
 * its contact sheet scrolls. State lives in useCarouselStore, so everything
 * survives in-portal navigation until a full reload.
 *
 * A phone gets the same composer as three steps — Pick, Review, Export — driven
 * by s.step. Desktop keeps the Library/Carousel toggle and the export sheet.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  cancelAssign, closeExport, closePeek, commitAssign, docPayload, docSaved, draftPayload,
  flushSave, focusTile, getState, hasWork, initUI, loadFiles, offerDraft, openExport, openPeek,
  pickFolder, runExport, selectImgById, selectVisible, setExportOpts, setFilters, setHelpOpen,
  setSaveScopeFor, setSize, setStep, setView, stepPeek, stopExport, toggleSel, useCarousel,
} from '../hooks/useCarouselStore';
import { useSaveCarousel } from '../hooks/useCarousels';
import { useCarouselDraft, useSaveCarouselDraft } from '../hooks/useCarouselDraft';
import { useMobile } from '../hooks/useMobile';
import { TAB_HEIGHT } from '../components/layout/BottomTabBar';
import { layoutName } from '../lib/carousel/geometry.js';
import {
  EXPORT_LABELS, canPickFolder, exportNoteText, outDims, sanitizePrefix, saveButtonLabel,
  slideFilename, slideHasPhotos,
} from '../lib/carousel/exportRender.js';
import CarouselHeader from '../components/carousel/CarouselHeader';
import HelpPopover from '../components/carousel/HelpPopover';
import LibraryView from '../components/carousel/LibraryView';
import Filmstrip from '../components/carousel/Filmstrip';
import CarouselView from '../components/carousel/CarouselView';
import PeekOverlay from '../components/carousel/PeekOverlay';
import ExportDialog from '../components/carousel/ExportDialog';
import DraftBanner from '../components/carousel/DraftBanner';
import EditPhotoDialog from '../components/carousel/EditPhotoDialog';
import ShootPickerDialog from '../components/carousel/ShootPickerDialog';
import CarouselsDialog from '../components/carousel/CarouselsDialog';
import SlideStage, { useSlidePreviews } from '../components/carousel/SlideStage';
import Toast from '../components/carousel/Toast';
import { afterRender, scrollPageTop } from '../components/carousel/dom.js';
import '../components/carousel/carousel.css';
import '../components/carousel/mobile.css';

// Which of the phone's three steps is on screen. `view` is still the truth for
// library vs carousel — the cell picker flips it on its own — so read it back
// from there and keep s.step only for the one it cannot express.
function stepOf(s) {
  if (s.step === 'export') return 'export';
  return s.view === 'library' ? 'pick' : 'review';
}

// Free navigation: nothing is ever disabled, and leaving the picker mid-choice
// counts as cancelling it rather than silently dropping the swap.
function goStep(k) {
  const s = getState();
  if (k !== 'pick' && s.assign) cancelAssign();
  setStep(k);
  setView(k === 'pick' ? 'library' : 'carousel');
}

function onMobile() {
  return !!document.querySelector('.cc-page.is-mobile');
}

// A portal dialog, menu or drawer that is not the composer's own export sheet.
function foreignModalOpen() {
  return !!document.querySelector('.MuiModal-root:not(.cc-modal):not(.MuiModal-hidden)');
}

// The composer's own sheets — shoot picker, carousels, photo editor, layout
// gallery: MUI handles Escape for them, and nothing else should reach the page
// while one is up. EVERY sheet belongs in this list. They are MUI modals carrying
// .cc-modal, so foreignModalOpen() deliberately does not catch them, and a sheet
// left out of here has the page's shortcuts running live behind it — 'g' and '/'
// switch the view underneath, which unmounts whatever owns the sheet's open state.
function ownSheetOpen() {
  return !!document.querySelector('.cc-shoots, .cc-docs, .cc-ed, .cc-layouts');
}

function onKeyDown(e) {
  const t = e.target;
  const inField = /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable;
  if (foreignModalOpen() || ownSheetOpen()) return;
  let s = getState();

  if (e.key === 'Escape') {
    if (s.peek) { closePeek(); return; }
    if (s.exportOpen) { if (!s.exportRunning) closeExport(); return; }
    if (s.helpOpen) { setHelpOpen(false); return; }
    if (s.filters.q) { setFilters({ q: '' }); return; }
    if (s.assign) { cancelAssign(); return; }
    if (s.view === 'library' && s.library.length) setView('carousel');
    return;
  }

  if (s.exportOpen) return;
  // Cmd/Ctrl+K and friends belong to the portal
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (!inField && !s.peek) {
    if (e.key === '?') { e.preventDefault(); setHelpOpen(!s.helpOpen); return; }
    // on a phone Export is a step, not a sheet
    if (e.key === 'e' || e.key === 'E') { e.preventDefault(); if (onMobile()) goStep('export'); else openExport(); return; }
  }

  if (s.peek) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); stepPeek(-1); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); stepPeek(1); return; }
    if (e.key === ' ' && !inField && s.peek.length === 1) { e.preventDefault(); toggleSel(s.peek[0]); return; }
    if (!inField) return;
  }

  if (t.dataset && t.dataset.ccSearch) {
    const vis = selectVisible(s);
    if (e.key === 'Enter' && vis.length) {
      e.preventDefault();
      if (s.assign) commitAssign(vis[0].id); else toggleSel(vis[0].id);
      setFilters({ q: '' });
    }
    return;
  }
  if (inField) return;

  if (e.key === 'g' || e.key === 'G') { setView(s.view === 'library' ? 'carousel' : 'library'); return; }
  if (e.key === '/') {
    e.preventDefault();
    setView('library');
    afterRender(() => { const box = document.querySelector('.cc-page [data-cc-search]'); if (box) box.focus(); });
    return;
  }
  if (e.key === '[') { setSize(s.size - 1); return; }
  if (e.key === ']') { setSize(s.size + 1); return; }
  const vis = selectVisible(s);
  if (s.view !== 'library' || !vis.length) return;
  if ((e.key === ' ' || e.key === 'Enter') && t.closest && t.closest('button,[role=button],a[href]')) return;

  const order = vis.map((im) => im.id);
  let i = order.indexOf(s.focusId);
  if (i < 0) i = 0;
  const tiles = new Map();
  document.querySelectorAll('.cc-page .sheet-grid .tile').forEach((el) => { tiles.set(el.dataset.id, el); });
  const moveTo = (j) => {
    const k = Math.min(order.length - 1, Math.max(0, j));
    focusTile(order[k], e.shiftKey);
    const el = tiles.get(order[k]);
    if (el) el.scrollIntoView({ block: 'nearest' });
  };
  if (e.key === 'ArrowRight') { e.preventDefault(); moveTo(i + 1); }
  else if (e.key === 'ArrowLeft') { e.preventDefault(); moveTo(i - 1); }
  else if (e.key === 'Home') { e.preventDefault(); moveTo(0); }
  else if (e.key === 'End') { e.preventDefault(); moveTo(order.length - 1); }
  else if (e.key === ' ') { e.preventDefault(); toggleSel(order[i]); }
  else if (e.key === 'Enter') {
    e.preventDefault();
    s = getState();
    if (s.assign) commitAssign(order[i]); else openPeek([order[i]]);
  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    const down = e.key === 'ArrowDown';
    const cur = tiles.get(order[i]);
    if (!cur) { moveTo(i + (down ? 1 : -1)); return; }
    const top = cur.offsetTop, left = cur.offsetLeft;
    let best = -1, bestDx = Infinity;
    order.forEach((id, j) => {
      const el = tiles.get(id);
      if (!el) return;
      const rowOk = down ? el.offsetTop > top : el.offsetTop < top;
      if (!rowOk) return;
      const dx = Math.abs(el.offsetLeft - left) + Math.abs(el.offsetTop - top) * 4;
      if (dx < bestDx) { bestDx = dx; best = j; }
    });
    if (best >= 0) moveTo(best);
  }
}

/**
 * The export sheet's content as the phone's third step. Same options, same rows
 * and the same runExport — only the shell differs, so nothing about how a slide
 * is rendered or named is forked. `.cc-export` and `.xrow[data-si]` stay because
 * runExport scrolls the running row into view through those selectors.
 */
function ExportStep({ onStep }) {
  const slides = useCarousel((s) => s.slides);
  const panos = useCarousel((s) => s.panos);
  const design = useCarousel((s) => s.design);
  const exportOpts = useCarousel((s) => s.exportOpts);
  const status = useCarousel((s) => s.exportStatus);
  const running = useCarousel((s) => s.exportRunning);
  const stopping = useCarousel((s) => s.exportStop);
  const imgById = useCarousel(selectImgById);
  const outDir = useCarousel((s) => s.outDir);
  const libN = useCarousel((s) => s.library.length);
  const [prefixRaw, setPrefixRaw] = useState(exportOpts.prefix);
  // this step is the last look before the files are written, so its rows paint the
  // pixels that will actually save rather than the thumbnail approximation
  const pv = useSlidePreviews(slides, panos, imgById);

  // arriving from Review keeps that view's scroll, which would hide the options
  useLayoutEffect(() => { scrollPageTop(false); }, []);

  useEffect(() => {
    setPrefixRaw((r) => (sanitizePrefix(r) === exportOpts.prefix ? r : exportOpts.prefix));
  }, [exportOpts.prefix]);

  const states = slides.map((sl) => (
    slideHasPhotos(sl, panos, imgById) ? (status[sl.id] || 'ready') : 'empty'
  ));
  const total = states.filter((st) => st !== 'empty').length;
  const savedN = states.filter((st) => st === 'saved').length;
  const pending = states.map((st, i) => (st !== 'saved' && st !== 'empty' ? i : -1)).filter((i) => i >= 0);
  const asZip = exportOpts.bundle === 'zip';
  const runLabel = running
    ? (stopping ? 'Stopping…' : 'Stop after this one')
    : !total ? 'Nothing to save' : !pending.length ? 'All saved'
      : (savedN ? 'Save remaining ' + pending.length : 'Save all ' + pending.length) + (asZip ? ' as zip' : '');

  // Free navigation means you can land here with nothing built: say what is
  // missing and offer the step that fixes it, rather than a dead button.
  if (!slides.length) {
    const next = libN ? 'review' : 'pick';
    return (
      <section className="cc-xstep cc-export">
        <div className="cc-xempty">
          <h2>Nothing to export yet</h2>
          <p>
            {libN
              ? 'Your carousel has no slides. Go to Review and add a few from the ' + libN + ' photos you have loaded.'
              : 'Start in Pick: load a delivered shoot or photos from this phone, then build slides in Review.'}
          </p>
          <button type="button" className="btn primary" onClick={() => onStep(next)}>
            {libN ? 'Go to Review' : 'Go to Pick'}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="cc-xstep cc-export">
      <div className="cc-xhead">
        <h2>Export carousel</h2>
        <span className="hint">
          {total
            ? savedN + ' of ' + total + ' saved · ' + outDims(design, exportOpts) + ' ' + (exportOpts.type === 'png' ? 'PNG' : 'JPEG')
            : 'No slide has a photo in it yet — go back to Review and fill one.'}
        </span>
      </div>
      <div className="export-opts">
        <label className="ctl">
          <span className="lbl">Size</span>
          <select value={String(exportOpts.qual)} disabled={running} onChange={(e) => setExportOpts({ qual: +e.target.value })}>
            <option value="1080">1080 px</option>
            <option value="1440">1440 px · Instagram max</option>
            <option value="2160">2160 px</option>
          </select>
        </label>
        <label className="ctl">
          <span className="lbl">File</span>
          <select value={exportOpts.type} disabled={running} onChange={(e) => setExportOpts({ type: e.target.value })}>
            <option value="jpg">JPEG</option>
            <option value="png">PNG · lossless</option>
          </select>
        </label>
        <label className="ctl cc-xdeliver">
          <span className="lbl">Deliver</span>
          <div className="seg">
            <button
              type="button"
              aria-pressed={exportOpts.bundle !== 'zip'}
              disabled={running}
              onClick={() => setExportOpts({ bundle: 'files' })}
            >
              Files
            </button>
            <button
              type="button"
              aria-pressed={asZip}
              disabled={running}
              onClick={() => setExportOpts({ bundle: 'zip' })}
            >
              One zip
            </button>
          </div>
        </label>
        <label className="ctl cc-xname">
          <span className="lbl">Name</span>
          <input
            type="text"
            className="prefix"
            value={prefixRaw}
            disabled={running}
            onChange={(e) => { setPrefixRaw(e.target.value); setExportOpts({ prefix: sanitizePrefix(e.target.value) }); }}
          />
        </label>
        {canPickFolder() && (
          <button type="button" className="btn tiny ghost" onClick={pickFolder}>Choose output folder</button>
        )}
      </div>
      <ol className="export-list">
        {slides.map((sl, si) => {
          const st = states[si];
          return (
            <li key={sl.id} className="xrow" data-si={si}>
              <span className="xnum mono">{String(si + 1).padStart(2, '0')}</span>
              <span className="xthumb">
                <SlideStage
                  sl={sl}
                  si={si}
                  design={design}
                  group={sl.pano ? panos[sl.pano.gid] : null}
                  imgById={imgById}
                  interactive={false}
                  graded
                  previews={pv}
                />
              </span>
              <div className="xinfo">
                <span className="xname">{slideFilename(si, exportOpts)}</span>
                <span className="xmeta lbl">{layoutName(sl, panos)}</span>
              </div>
              <span className="xstatus" data-state={st}>{EXPORT_LABELS[st]}</span>
              <button
                type="button"
                className="btn tiny ghost xsave"
                disabled={running || st === 'empty'}
                onClick={() => runExport([si], { forceFiles: true })}
              >
                {saveButtonLabel(st)}
              </button>
            </li>
          );
        })}
      </ol>
      <div className="cc-xfoot">
        <span className="hint">{exportNoteText({ current: outDir }, exportOpts)}</span>
        <button
          type="button"
          className="btn primary"
          disabled={!running && !pending.length}
          onClick={() => { if (running) stopExport(); else runExport(pending, { logRun: true }); }}
        >
          {runLabel}
        </button>
      </div>
    </section>
  );
}

export default function CarouselPage() {
  useState(initUI);
  const { isMobile } = useMobile();
  const view = useCarousel((s) => s.view);
  const step = useCarousel(stepOf);
  const libN = useCarousel((s) => s.library.length);
  const helpOpen = useCarousel((s) => s.helpOpen);
  const doc = useCarousel((s) => s.doc);
  const docDirty = useCarousel((s) => s.docDirty);
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const fileRef = useRef(null);
  const [shootsOpen, setShootsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const saveMut = useSaveCarousel();
  const saveRef = useRef(saveMut);
  saveRef.current = saveMut;
  const rev = useCarousel((s) => s.rev);
  const { data: draftData } = useCarouselDraft();
  const draftMut = useSaveCarouselDraft();
  const draftRef = useRef(draftMut);
  draftRef.current = draftMut;

  useEffect(() => {
    const head = headRef.current, root = rootRef.current;
    // arriving through the sidebar leaves focus on its link, which would take Space/Enter
    const active = document.activeElement;
    if (active && active !== document.body && root && !root.contains(active) && active.blur) active.blur();
    let ro = null;
    if (head && root && window.ResizeObserver) {
      ro = new ResizeObserver(() => {
        const h = head.offsetHeight;
        if (h > 0) root.style.setProperty('--headh', h + 'px');
      });
      ro.observe(head);
    }
    const onDragOver = (e) => e.preventDefault();
    const onDrop = (e) => {
      if (e.target.closest && e.target.closest('.cell')) return;
      e.preventDefault();
      if (e.dataTransfer.files.length) loadFiles(e.dataTransfer.files);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      flushSave();
    };
  }, []);

  // Whose autosave this is — the browser copy is keyed by it too — and the offer
  // to pick the work back up, which only shows over an empty composer.
  useEffect(() => {
    if (!draftData) return;
    setSaveScopeFor(draftData.profileId);
    offerDraft(draftData.draft);
  }, [draftData]);

  // The per-user autosave: references only, a few seconds after you stop working.
  // An empty composer never overwrites it, so a stray "remove all" is recoverable.
  useEffect(() => {
    if (!rev || !hasWork()) return undefined;
    const t = setTimeout(() => { draftRef.current.mutate(draftPayload()); }, 3000);
    return () => {
      clearTimeout(t);
      // leaving within the debounce still saves what is on screen
      if (hasWork()) draftRef.current.mutate(draftPayload());
    };
  }, [rev]);

  // An open saved carousel keeps itself up to date; a failed write just leaves it
  // dirty, and the Carousels sheet still has a Save button.
  useEffect(() => {
    if (!doc || !docDirty) return undefined;
    const t = setTimeout(async () => {
      try {
        const row = await saveRef.current.mutateAsync({ id: doc.id, ...docPayload(doc.title, doc.jobId) });
        docSaved(row, { quiet: true });
      } catch { /* stays dirty until the next edit or an explicit Save */ }
    }, 2500);
    return () => clearTimeout(t);
  }, [doc, docDirty]);

  const choose = () => fileRef.current?.click();
  const onExportStep = isMobile && step === 'export';

  const cls = 'cc-root cc-page'
    + (view === 'library' ? ' view-library' : '')
    + (libN ? '' : ' lib-empty')
    + (isMobile ? ' is-mobile' : '');

  let body;
  if (onExportStep) body = <ExportStep onStep={goStep} />;
  else if (view === 'library') body = <LibraryView onChoose={choose} onShoots={() => setShootsOpen(true)} onStep={goStep} />;
  else body = <CarouselView />;

  return (
    <Box
      ref={rootRef}
      className={cls}
      sx={{ mt: isMobile ? -1.5 : -3, mx: isMobile ? -1.5 : -3, mb: isMobile ? `-${TAB_HEIGHT + 16}px` : -3 }}
    >
      <CarouselHeader
        headRef={headRef}
        mobile={isMobile}
        step={step}
        onStep={goStep}
        onAddPhotos={choose}
        onShoots={() => setShootsOpen(true)}
        onDocs={() => setDocsOpen(true)}
      />
      <DraftBanner />
      {helpOpen && <HelpPopover />}
      {/* the library strip and the composer belong to Pick: on a phone Review
          spent 232px of sticky chrome before its first slide, and "Browse all" is
          one tap on the Pick chip away */}
      {view === 'carousel' && libN > 0 && !onExportStep && !isMobile && <Filmstrip />}
      {body}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => { loadFiles(e.target.files); e.target.value = ''; }}
      />
      <PeekOverlay />
      <ExportDialog />
      <EditPhotoDialog />
      <ShootPickerDialog
        open={shootsOpen}
        onClose={() => setShootsOpen(false)}
        suggest={doc ? doc.title : ''}
      />
      <CarouselsDialog open={docsOpen} onClose={() => setDocsOpen(false)} />
      <Toast />
    </Box>
  );
}
